import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Check, Calendar, Users, MapPin, Copy, Share2 } from "lucide-react";

import { toast } from "sonner";

export default function BookingConfirmation() {
  const location = useLocation();
  const { booking, tour } = location.state || {};
  const [copied, setCopied] = useState(false);

  const copyReferral = () => {
    if (!booking?.referral_code) return;
    const msg = `Book an adventure in Saint Lucia with Keyz's Epic Adventure Tours! Use my code ${booking.referral_code} for 15% off: ${window.location.origin}/tours`;
    navigator.clipboard.writeText(msg).then(() => {
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const shareWhatsApp = () => {
    if (!booking?.referral_code) return;
    const msg = encodeURIComponent(`Book an adventure in Saint Lucia with Keyz's Epic Adventure Tours! Use my code ${booking.referral_code} for 15% off: ${window.location.origin}/tours`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  if (!booking) {
    return (
      <div className="min-h-screen bg-bone">
        <Navbar />
        <div className="pt-28 px-6 max-w-2xl mx-auto text-center py-24">
          <p className="font-cormorant text-3xl text-midnight mb-4">No Booking Found</p>
          <Link to="/tours" data-testid="browse-tours-link">
            <Button className="rounded-full bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-sm">
              Browse Tours
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      <div className="pt-28 px-6 max-w-2xl mx-auto py-16">
        {/* Success Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-full bg-canopy/10 flex items-center justify-center">
            <Check className="w-8 h-8 text-canopy" />
          </div>
        </div>

        <div className="text-center mb-12">
          <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-3">Confirmed</p>
          <h1 className="font-cormorant text-3xl sm:text-4xl font-light text-midnight mb-3" data-testid="confirmation-title">
            Booking Confirmed!
          </h1>
          <p className="font-manrope text-sm text-midnight/50">
            Your adventure awaits. A confirmation has been sent to {booking.customer_email}.
          </p>
        </div>

        {/* Booking Details */}
        <div className="bg-white border border-midnight/5 p-8" data-testid="booking-details">
          <h2 className="font-cormorant text-2xl font-light text-midnight mb-6">{booking.tour_title}</h2>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="flex items-start gap-3">
              <Calendar className="w-4 h-4 text-sandy mt-0.5" />
              <div>
                <p className="font-manrope text-xs uppercase tracking-widest text-midnight/40">Date</p>
                <p className="font-manrope text-sm text-midnight">{booking.date}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="w-4 h-4 text-sandy mt-0.5" />
              <div>
                <p className="font-manrope text-xs uppercase tracking-widest text-midnight/40">Guests</p>
                <p className="font-manrope text-sm text-midnight">{booking.guests}</p>
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          <div className="flex items-center justify-between mb-2">
            <span className="font-manrope text-sm text-midnight/60">Booking ID</span>
            <span className="font-manrope text-xs text-midnight/40">{booking.booking_id}</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-manrope text-sm text-midnight/60">Payment</span>
            <span className="font-manrope text-xs text-sandy uppercase">Mock PayPal</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-manrope text-sm text-midnight/60">Status</span>
            <span className="font-manrope text-xs text-canopy uppercase">{booking.status}</span>
          </div>

          <Separator className="my-6" />

          <div className="flex items-center justify-between">
            <span className="font-manrope text-sm font-medium text-midnight">Total Paid</span>
            <span className="font-cormorant text-2xl text-midnight">${booking.total_price?.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex justify-center mt-8 gap-4">
          <Link to="/my-bookings" data-testid="view-bookings-btn">
            <Button variant="outline" className="rounded-full font-manrope text-sm px-6">
              My Bookings
            </Button>
          </Link>
          <Link to="/tours" data-testid="back-to-tours-btn">
            <Button className="rounded-full bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-sm px-6">
              Browse More Tours
            </Button>
          </Link>
        </div>

        {/* Share Your Adventure */}
        {booking.referral_code && (
          <div className="mt-12 bg-white border border-sandy/20 p-8 text-center" data-testid="share-adventure-section">
            <Share2 className="w-6 h-6 text-sandy mx-auto mb-3" />
            <h3 className="font-cormorant text-2xl font-light text-midnight mb-2">Share Your Adventure</h3>
            <p className="font-manrope text-sm text-midnight/50 mb-4 max-w-sm mx-auto">
              Give friends 15% off their first Saint Lucia excursion with your personal referral code.
            </p>
            <div className="inline-flex items-center gap-2 bg-bone border border-sandy/20 px-6 py-3 mb-4">
              <span className="font-manrope text-lg font-semibold tracking-widest text-sandy" data-testid="referral-code-display">
                {booking.referral_code}
              </span>
            </div>
            <div className="flex justify-center gap-3 mt-4">
              <button
                data-testid="share-copy-btn"
                onClick={copyReferral}
                className="flex items-center gap-2 px-5 py-2.5 border border-midnight/10 font-manrope text-sm text-midnight hover:border-sandy transition-colors duration-200"
              >
                {copied ? <Check className="w-4 h-4 text-canopy" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied!" : "Copy Link"}
              </button>
              <button
                data-testid="share-whatsapp-btn"
                onClick={shareWhatsApp}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#25D366] text-white font-manrope text-sm hover:bg-[#25D366]/90 transition-colors duration-200"
              >
                <Share2 className="w-4 h-4" />
                Share on WhatsApp
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
